/**
 * Serviço de Consulta de Dados de Empresa
 */
const getToken = () => {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem("token");
};

/**
 * Carrega dados de uma empresa por id, independente de quem esteja
 * logado. Usado pelo fluxo de "busca interna" do Dashboard: o usuário
 * CIEE clica num resultado da busca e a página de formulário (TCE,
 * TCA, TcaEmpregador) é aberta com `?empresaId=X`, caindo no modo
 * "novo" mas já com os dados da empresa X pré-preenchidos.
 *
 * O backend decide qual rota expor para esta carga — hoje usamos o
 * `/api/empresa/buscar-interno` (retorna a empresa com base no id),
 * mas mantemos fallback para `/tca/dados-empresa?empresaId=X` se o
 * backend não tiver rota dedicada.
 *
 * @param {string|number} empresaId
 * @param {{ signal?: AbortSignal }} [opts]
 * @returns {Promise<object|null>} dados da empresa ou `null` em erro
 */
export async function carregarDadosEmpresaPorId(empresaId, opts = {}) {
  if (empresaId == null) return null;
  const token = getToken();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  // 1) Rota dedicada da busca interna (mesma que o Dashboard usa).
  try {
    const { API_BASE_URL } = await import("../../js/api.js");
    const resp = await fetch(
      `${API_BASE_URL}/api/empresa/buscar-interno?campo=id&valor=${encodeURIComponent(
        String(empresaId),
      )}&limite=1`,
      { headers, signal: opts.signal },
    );
    if (resp.ok) {
      const json = await resp.json().catch(() => null);
      const lista = Array.isArray(json?.data) ? json.data : [];
      if (lista.length > 0) {
        const item = lista[0];
        if (item.logradouro || item.endereco) return item;
        try {
          const tentativas = [
            `${API_BASE_URL}/tce/empresa-endereco?empresaId=${encodeURIComponent(String(empresaId))}`,
            `${API_BASE_URL}/tce/dados-empresa?empresaId=${encodeURIComponent(String(empresaId))}`,
            `${API_BASE_URL}/tca/dados-empresa?empresaId=${encodeURIComponent(String(empresaId))}`,
          ];
          for (const url of tentativas) {
            const respFall = await fetch(url, { headers, signal: opts.signal });
            if (respFall.ok) {
              const dataFall = await respFall.json().catch(() => null);
              if (dataFall) {
                return { ...dataFall, ...item, ...(dataFall.endereco_matriz || {}), ...dataFall };
              }
            }
          }
        } catch (_) {}
        return item;
      }
    }
  } catch {
    // cai no fallback abaixo
  }

  // 2) Fallback: tentar endpoints existentes que aceitam ?empresaId=
  try {
    const { API_BASE_URL } = await import("../../js/api.js");
    const tentativas = [
      `${API_BASE_URL}/tce/empresa-endereco?empresaId=${encodeURIComponent(String(empresaId))}`,
      `${API_BASE_URL}/tca/dados-empresa?empresaId=${encodeURIComponent(String(empresaId))}`,
      `${API_BASE_URL}/tce/dados-empresa?empresaId=${encodeURIComponent(String(empresaId))}`,
    ];
    for (const url of tentativas) {
      const resp = await fetch(url, { headers, signal: opts.signal });
      if (resp.ok) {
        const data = await resp.json().catch(() => null);
        if (data) return data;
      }
    }
  } catch {
    // ignora
  }

  return null;
}
